import { Divider } from 'antd'
import CustomTag from 'components/CustomTag'
import CalendarYellow from 'components/icons/CalendarYellow'
import ConstructionIcon from 'components/icons/ConstructionIcon'
import Location from 'components/icons/Location'
import PaymentIcon from 'components/icons/PaymentIcon'
import {
  BoldText,
  Container1,
  Content,
  ExtraPadding,
  FlexWrap,
  GrayText,
  GrayTextSmall,
  InfoText,
  ShowLessText,
  ShowMoreText,
  SkillList,
  SubBold,
  SubtitleMedium,
  SubtitleSmall,
  YearSpan,
  YellowBox,
} from 'modules/Roles/NewRoleCreation/Partials/PreviewRoleDrawer/Style/style'
import React, { useEffect, useRef, useState } from 'react'
import { COLORS } from 'theme'
import { FONT } from 'theme/font'
import { formatNumber } from 'utils/formatNumber'
import { getDay } from 'utils/getDay'
import { toCamelCase } from '../../../../../../../utils/camelCase'

const ContentRight = ({
  jobSection = false,
  previewRoleData,
  endDate,
  cities,
  mandatorySkills,
  optionalSkills,
  isRemoteAllowed,
  remoteCities,
  degrees,
  specialisations,
  yearOfPassing,
  isArrearsAllowed,
  arrearCount,
  avgScore,
  tenthScore,
  twelthScore,
  UGScore,
  PGScore,
  minCompensation,
  maxCompensation,
  isHybridAllowed,
  hybridType,
  hybridDays,
  ctcPeriod,
  isAnyWhere,
  publishTo,
  candidatesCount,
  arrearCondition,
  isHistoryArrears,
  historyArrearCount,
}) => {
  const specialisationsData = specialisations?.map(
    item => item.specialisationsName
  )
  const groupedEducations = Array.isArray(degrees)
    ? degrees?.reduce((acc, education) => {
        const degreeShortForm = education?.degree?.shortForm
        const streamName = education?.stream?.name

        if (!acc.hasOwnProperty(degreeShortForm)) {
          acc[degreeShortForm] = []
        }

        acc[degreeShortForm].push(streamName)

        return acc
      }, {})
    : []

  const renderContent = () => {
    let content = []

    if (groupedEducations) {
      Object.entries(groupedEducations).forEach(([degree, streamNames]) => {
        content.push(
          <ExtraPadding key={degree}>
            <SubBold>{degree}</SubBold>
            <br />
            {streamNames.join(', ')}
            <br />
          </ExtraPadding>
        )
      })
    }

    return content
  }

  const [expanded, setExpanded] = useState(true)
  const hasNonEmptySkillSet = mandatorySkills?.otherSkillsNames?.some(
    skillSet => skillSet?.skillNames?.length > 0
  )
  const toggleExpanded = () => {
    setExpanded(!expanded)
  }

  const contentHeight = useRef(null)

  useEffect(() => {
    if (contentHeight.current && contentHeight.current.clientHeight > 250) {
      setExpanded(false)
    }
  }, [])

  let infoTextContent

  if (isAnyWhere) {
    infoTextContent = 'AnyWhere'
  } else if (Array.isArray(cities) && cities.length > 0) {
    infoTextContent = cities
      .map(city => city.name ?? city.state_name)
      .join(', ')
  } else {
    infoTextContent = null
  }

  return (
    <>
      {jobSection ? (
        <FlexWrap gap="5px" column>
          <FlexWrap gap="5px" centerVertically>
            <PaymentIcon />
            <SubtitleMedium>CTC</SubtitleMedium>
          </FlexWrap>
          <SubtitleSmall
            fontsize="18px"
            marginbottom="0"
            color={COLORS.GREY_T_10}
          >
            {minCompensation == maxCompensation ? (
              <span>{formatNumber(Number(minCompensation))}</span>
            ) : (
              <span>
                ₹ {formatNumber(Number(minCompensation))} - ₹{' '}
                {formatNumber(Number(maxCompensation))}
              </span>
            )}
            {!!ctcPeriod && `${ctcPeriod}`}
          </SubtitleSmall>
        </FlexWrap>
      ) : (
        <YellowBox>
          <CalendarYellow />
          <FlexWrap column>
            <GrayText fontweight={FONT.FW_400}>Last date to apply</GrayText>
            <SubtitleSmall fontweight={FONT.FW_600}>
              {getDay(endDate, true)}
            </SubtitleSmall>
          </FlexWrap>
        </YellowBox>
      )}
      <Divider />
      <div>
        <FlexWrap gap="10px" centerVertically>
          <Location />
          <SubtitleMedium marginbottom={'5px'}>Job Location</SubtitleMedium>
        </FlexWrap>
        {infoTextContent && (
          <InfoText paddingTop="5px" marginleft="10px" capitalize={true}>
            {infoTextContent}
          </InfoText>
        )}
      </div>

      {isRemoteAllowed ? (
        <>
          <SubtitleSmall fontsize={FONT.XS} fontweight={600}>
            We are also remote friendly
          </SubtitleSmall>
          <FlexWrap gap="3px" column>
            <BoldText>Remote Location :</BoldText>
            {remoteCities && remoteCities.length > 0 ? (
              <InfoText marginleft="10px" capitalize={true}>
                {remoteCities
                  .map(city => city.name ?? city?.state_name)
                  .join(', ')}
              </InfoText>
            ) : null}
          </FlexWrap>
        </>
      ) : null}

      {isHybridAllowed ? (
        <>
          <SubtitleSmall fontsize={FONT.XS} fontweight={600}>
            We allow hybrid
          </SubtitleSmall>
          <FlexWrap gap="8px">
            <BoldText>Work from office:</BoldText>
            <SubtitleSmall>
              {hybridDays} days / {hybridType}.
            </SubtitleSmall>
          </FlexWrap>
        </>
      ) : null}

      <Divider />

      {/* Gender and Number of Candidates Section */}
      {(publishTo || candidatesCount) && (
        <div style={{ marginBottom: '20px' }}>
          {publishTo && (
            <div style={{ marginBottom: '15px' }}>
              <GrayTextSmall>Gender</GrayTextSmall>
              <SubtitleSmall marginbottom="0" capitalize={true}>
                {publishTo === 'ALL' ? 'All' : 
                 publishTo === 'MALE' ? 'Male' : 
                 publishTo === 'FEMALE' ? 'Female' : 
                 publishTo}
              </SubtitleSmall>
            </div>
          )}
          {candidatesCount && (
            <div style={{ marginBottom: '15px' }}>
              <GrayTextSmall>Number of Candidates to be Hired</GrayTextSmall>
              <SubtitleSmall marginbottom="0" capitalize={true}>
                {candidatesCount}
              </SubtitleSmall>
            </div>
          )}
        </div>
      )}

      <FlexWrap gap="10px" centerVertically>
        <ConstructionIcon />
        <SubtitleMedium>Eligibility Criteria</SubtitleMedium>
      </FlexWrap>
      {(mandatorySkills?.primarySkillNames?.skillNames?.length > 0 ||
        mandatorySkills?.mandatorySkillNames?.skillNames?.length > 0 ||
        mandatorySkills?.otherSkillsNames?.skillNames?.length > 0) && (
        <div>
          <GrayTextSmall>Mandatory Skills</GrayTextSmall>
          <SkillList>
            {mandatorySkills?.primarySkillNames?.skillNames?.length ? (
              <SubtitleSmall
                fontsize={FONT.XS}
                padding="0px 8px"
                fontweight={FONT.FW_600}
              >
                <li>Primary Mandatory Skills</li>
              </SubtitleSmall>
            ) : (
              ''
            )}
            <FlexWrap wrap gap="1px" padding="1px 0px 0px 0px">
              {mandatorySkills?.primarySkillNames?.skillNames?.map(
                (tag, index) => (
                  <>
                    <CustomTag
                      key={index + 'primaryMandatory'}
                      message={tag.name}
                      marginright={'3px'}
                      paddingTop={'8px'}
                    />
                    {mandatorySkills?.primarySkillNames?.skillNames?.length !=
                      index + 1 && (
                      <InfoText marginRight="4px">
                        {mandatorySkills?.primarySkillNames?.type == 'AND'
                          ? '&'
                          : 'OR'}
                      </InfoText>
                    )}
                  </>
                )
              )}
            </FlexWrap>
            {mandatorySkills?.mandatorySkillNames?.length ? (
              <SubtitleSmall
                fontsize={FONT.XS}
                padding="0px 8px"
                fontweight={FONT.FW_600}
              >
                <li>Secondary Mandatory Skills</li>
              </SubtitleSmall>
            ) : (
              ''
            )}

            <FlexWrap wrap gap="1px" padding="1px 0px 5px 0px">
              {mandatorySkills?.mandatorySkillNames?.map((tag, index) => (
                <>
                  <CustomTag
                    key={index + 'mandatorySkills'}
                    message={tag.name}
                    marginright={'3px'}
                  />
                  {mandatorySkills?.mandatorySkillNames?.length !=
                    index + 1 && <InfoText marginRight="4px">,</InfoText>}
                </>
              ))}
            </FlexWrap>
            {hasNonEmptySkillSet &&
            mandatorySkills?.otherSkillsNames?.length ? (
              <SubtitleSmall
                fontsize={FONT.XS}
                padding="0px 8px"
                fontweight={FONT.FW_600}
              >
                <li>Other Mandatory Skills</li>
              </SubtitleSmall>
            ) : null}
            {hasNonEmptySkillSet && (
              <SkillList>
                {mandatorySkills?.otherSkillsNames?.map((skillSet, index) => (
                  <>
                    <SubtitleSmall
                      fontsize={FONT.XS}
                      padding="0px 8px"
                      fontweight={FONT.FW_500}
                    >
                      <li>Skill Set {index + 1}</li>
                    </SubtitleSmall>
                    <FlexWrap wrap gap="1px">
                      {skillSet?.skillNames?.map((tag, index2) => (
                        <>
                          <CustomTag
                            key={index + 'skillSet'}
                            message={tag.name}
                            marginright={'3px'}
                          />
                          {skillSet?.skillNames?.length != index2 + 1 && (
                            <div>
                              <InfoText marginRight="4px">
                                {skillSet?.type == 'AND' ? '&' : 'OR'}
                              </InfoText>
                            </div>
                          )}
                        </>
                      ))}
                    </FlexWrap>
                  </>
                ))}
              </SkillList>
            )}
          </SkillList>
        </div>
      )}

      {optionalSkills?.length ? (
        <>
          <GrayTextSmall>Optional Skills</GrayTextSmall>
          <FlexWrap wrap gap="5px">
            {optionalSkills?.map((tag, index) => (
              <CustomTag key={index + 'optionSkills'} message={tag} />
            ))}
          </FlexWrap>
        </>
      ) : null}

      <div>
        <GrayTextSmall>Gender</GrayTextSmall>
        <Content>{toCamelCase(publishTo)}</Content>
      </div>
      {degrees ? (
        <>
          <GrayTextSmall>Degree & Departments</GrayTextSmall>

          <Container1
            expanded={expanded}
            style={{
              height:
                contentHeight.current?.clientHeight < 250
                  ? 'auto'
                  : contentHeight.current?.clientHeight < 250 && '500px',
            }}
          >
            <Content ref={contentHeight}>{renderContent()}</Content>
          </Container1>

          {contentHeight.current?.clientHeight > 250 && (
            <ShowMoreText visible={!expanded} onClick={toggleExpanded}>
              Show More...
            </ShowMoreText>
          )}

          {contentHeight.current?.clientHeight > 250 && expanded && (
            <ShowLessText visible={expanded} onClick={toggleExpanded}>
              Show Less
            </ShowLessText>
          )}
        </>
      ) : null}

      <>
        {specialisations?.length > 0 && (
          <GrayTextSmall>Specialisations</GrayTextSmall>
        )}
        {specialisations?.length ? (
          <Content>{specialisationsData}</Content>
        ) : null}
      </>

      {yearOfPassing?.length ? (
        <div>
          <GrayTextSmall>Year of passing</GrayTextSmall>
          <YearSpan> {yearOfPassing?.join(', ')}</YearSpan>
        </div>
      ) : null}

      <div>
        <GrayTextSmall>Arrears / Backlogs</GrayTextSmall>
        {isArrearsAllowed && (
          <>
            <GrayTextSmall>Current Arrears</GrayTextSmall>
            <SubtitleSmall>Less than or equals to {arrearCount}.</SubtitleSmall>
          </>
        )}
        {isHistoryArrears && (
          <>
            <GrayTextSmall>History of Arrears</GrayTextSmall>
            <SubtitleSmall>
              Less than or equals to {historyArrearCount}.
            </SubtitleSmall>
          </>
        )}
        {!isArrearsAllowed && !isHistoryArrears && (
          <>
            <SubtitleSmall>No Arrears / Backlogs allowed</SubtitleSmall>
          </>
        )}
        {/* (
          <SubtitleSmall>No Arrears / Backlogs allowed</SubtitleSmall>
        ) */}
      </div>

      <div>
        <GrayTextSmall>Score</GrayTextSmall>
        {avgScore != 0 && (
          <SubtitleSmall>
            Average mark should be above {avgScore}% in all your exams
          </SubtitleSmall>
        )}
        {tenthScore ? (
          <SubtitleSmall>10th - Above {tenthScore}%</SubtitleSmall>
        ) : null}

        {twelthScore ? (
          <SubtitleSmall>
            12th or Equivalent - Above {twelthScore}%
          </SubtitleSmall>
        ) : null}

        {UGScore ? <SubtitleSmall>UG - Above {UGScore}%</SubtitleSmall> : null}
        {PGScore ? <SubtitleSmall>PG - Above {PGScore}%</SubtitleSmall> : null}
      </div>
    </>
  )
}

export default ContentRight
